import { PublicClientApplication } from '@azure/msal-browser';

let msalInstance: PublicClientApplication | null = null;
let msalConfig: { client_id: string; tenant_id: string } | null = null;
let initializationPromise: Promise<void> | null = null;
// Set once handleRedirectPromise() resolves with a token after the user
// completes Microsoft login and the browser navigates back to this page.
let redirectIdToken: string | null = null;

async function fetchMsalConfig(): Promise<{ client_id: string; tenant_id: string } | null> {
	try {
		const resp = await fetch('/v1/auth/microsoft-config/');
		const data = await resp.json();
		if (data.client_id) {
			return { client_id: data.client_id, tenant_id: data.tenant_id || 'common' };
		}
	} catch {
		// config endpoint unavailable
	}
	return null;
}

export async function initMsal(): Promise<void> {
	if (initializationPromise) return initializationPromise;

	initializationPromise = (async () => {
		const config = await fetchMsalConfig();
		if (!config) return;
		msalConfig = config;

		const instance = new PublicClientApplication({
			auth: {
				clientId: config.client_id,
				authority: `https://login.microsoftonline.com/${config.tenant_id}`,
				// Must land on /login specifically, not bare origin: the root
				// route ('/') renders RoleBasedLanding, which issues an
				// immediate <Navigate replace> on mount that strips the
				// "#code=..." fragment before handleRedirectPromise() below
				// gets a chance to read it. /login has no such redirect.
				redirectUri: `${window.location.origin}/login`,
			},
			cache: { cacheLocation: 'sessionStorage' },
		});

		await instance.initialize();
		msalInstance = instance;

		// Completes the loginRedirect() flow: when the browser navigates back
		// from Microsoft, this resolves with the token. sessionStorage carries
		// the in-flight request across the full page reload.
		const response = await instance.handleRedirectPromise().catch(() => null);
		if (response?.idToken) {
			redirectIdToken = response.idToken;
		}
	})();

	return initializationPromise;
}

export function getMsalConfig(): { client_id: string; tenant_id: string } | null {
	return msalConfig;
}

export function consumeRedirectIdToken(): string | null {
	const token = redirectIdToken;
	redirectIdToken = null;
	return token;
}

export function clearMsalState(): void {
	msalInstance = null;
	initializationPromise = null;
	redirectIdToken = null;
	const keys: string[] = [];
	for (let i = 0; i < sessionStorage.length; i++) {
		const key = sessionStorage.key(i);
		if (key && key.startsWith('msal.')) keys.push(key);
	}
	keys.forEach((k) => sessionStorage.removeItem(k));
}

export async function microsoftLoginRedirect(): Promise<void> {
	if (!msalInstance) {
		await initMsal();
	}
	if (!msalInstance) {
		throw new Error('Microsoft authentication is not configured.');
	}

	// Navigates the whole page to Microsoft's login; this call does not
	// resolve — the result is picked up via handleRedirectPromise() in
	// initMsal() on the next page load.
	await msalInstance.loginRedirect({
		scopes: ['openid', 'profile', 'email', 'User.Read'],
		prompt: 'select_account',
	});
}
